import { useState } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { CompaRatio } from '@mesh.ai/rabbit';
import { format, formatDistance, formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns';
import { createPortal } from 'react-dom';

import { Avatar } from 'uiToolkitV1/Avatar';
import { DateComp } from 'uiToolkitV1/DateComp';
import { DatePicker } from 'uiToolkitV1/DatePicker';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

export const EmployeeCellRenderer = ({
  value: { displayName, profilePhoto, mainRole },
}: {
  value: {
    displayName: string;
    profilePhoto: string;
    mainRole: string;
  };
}) => (
  <Flex alignItems="center" gap="8px" h="100%">
    <Avatar name={displayName} src={profilePhoto} size={24} />
    <Flex flexDirection="column">
      <TextBodyMedium>{displayName}</TextBodyMedium>
      <TextLabel color="text.g2">{mainRole}</TextLabel>
    </Flex>
  </Flex>
);

const RATING_TEXT_AND_COLORS = {
  AVERAGE_PERFORMER: {
    text: 'Average Performer',
    color: '#8C6A00',
    background: '#FFF6D3',
  },
  HIGH_PERFORMER: {
    text: 'High Performer',
    color: '#44812F',
    background: '#EFFFEA',
  },
  LOW_PERFORMER: {
    text: 'Low Performer',
    color: '#C62C02',
    background: '#FFF1E4',
  },
};

export const RatingCellRenderer = ({ value }: { value: string }) => (
  <Flex alignItems="center" h="100%">
    <Box p="4px 8px" w="fit-content" borderRadius="4px" backgroundColor={RATING_TEXT_AND_COLORS[value]?.background}>
      <TextLabel bold color={RATING_TEXT_AND_COLORS[value]?.color}>
        {RATING_TEXT_AND_COLORS[value]?.text}
      </TextLabel>
    </Box>
  </Flex>
);

const CHURN_TEXT_AND_COLORS = {
  HIGH: {
    text: 'High',
    color: '#C62C02',
    background: '#FFF1E4',
  },
  MEDIUM: {
    text: 'Medium',
    color: '#8C6A00',
    background: '#FFF6D3',
  },
  LOW: {
    text: 'Low',
    color: '#44812F',
    background: '#EFFFEA',
  },
};
export const ChurnLikelihoodCellRenderer = ({ value }: { value: string }) => (
  <Flex alignItems="center" h="100%">
    <Box p="4px 8px" w="fit-content" borderRadius="4px" backgroundColor={CHURN_TEXT_AND_COLORS[value]?.background}>
      <TextLabel bold color={CHURN_TEXT_AND_COLORS[value]?.color}>
        {CHURN_TEXT_AND_COLORS[value]?.text}
      </TextLabel>
    </Box>
  </Flex>
);

export const WaitingOnCellRenderer = ({
  value: { status, employee },
}: {
  value: {
    status: string;
    employee: {
      displayName: string;
      profilePhoto: string;
    };
  };
}) => (
  <Flex alignItems="center" gap="8px" h="100%">
    <Avatar name={employee.displayName} src={employee.profilePhoto} size={24} />
    <Flex flexDirection="column">
      <TextLabelSmall>{status}</TextLabelSmall>
      <TextBodyMedium>{employee.displayName}</TextBodyMedium>
    </Flex>
  </Flex>
);

export const COMPA_RATIO_CELL_RENDERER = ({ value }: { value: number }) => (
  <Flex alignItems="center" h="100%">
    <CompaRatio value={value} />
    <TextBodyMedium bold ml="8px">
      {(value * 100).toFixed(0)}%
    </TextBodyMedium>
  </Flex>
);

export const BaseRateCellRenderer = ({ value }: { value: { symbol: string; value: number; currency: string } }) => (
  <Flex alignItems="center" h="100%">
    <TextBodyMedium>
      {value.symbol}
      {value.value}
    </TextBodyMedium>
  </Flex>
);

export const RecommendationCellRenderer = ({
  value,
}: {
  value: { symbol: string; value: number; currency: string };
}) => (
  <Flex alignItems="center" h="100%">
    <TextBodyMedium>
      {value.symbol}
      {value.value}
    </TextBodyMedium>
  </Flex>
);

export const ProposedBaseSalaryCellRenderer = ({
  value,
}: {
  value: {
    symbol: string;
    value: number;
    currency: string;
    guidanceValue?: { minPercentage: number; maxPercentage: number };
  };
}) => {
  const [baseSalary, setBaseSalary] = useState(value.value);
  return (
    <Flex h="100%" flexDir="column">
      <InputV2
        isDashed
        type="number"
        value={baseSalary}
        leftComponent={<TextBodyMedium>{value.symbol}</TextBodyMedium>}
        onChange={(e) => setBaseSalary(Number(e.target.value))}
        rightComponent={
          <Flex alignItems="center">
            <Divider mr="8px" height="24px" background="#cfd7df" opacity={1} orientation="vertical" />
            <TextLabel>6%</TextLabel>
          </Flex>
        }
      />
      <TextLabelSmall bold color="#C62C02">
        Guidance value is
        {value.guidanceValue ? ` ${value.guidanceValue.minPercentage}% - ${value.guidanceValue.maxPercentage}%` : ''}
      </TextLabelSmall>
    </Flex>
  );
};

export const EffectiveDateCellRenderer = ({ value }: { value: string }) => (
  <Flex alignItems="center" h="100%">
    <DatePicker
      // onChange={handleDateChange}
      selectedDate={new Date(value)}
      popperPlacement="bottom-start"
      portalId="portal-root"
      // minDate={minDate}
      // maxDate={maxDate}
    />
  </Flex>
);

export const LastDateOfChangeCellRenderer = ({ value }: { value: string }) => (
  <Flex justifyContent="center" flexDir="column" h="100%">
    <TextBodyMedium>
      {formatDistanceToNowStrict(new Date(value), {
        addSuffix: true,
      })}
    </TextBodyMedium>
    <TextLabel color="text.g2">{format(new Date(value), 'MMM dd, yyyy')}</TextLabel>
  </Flex>
);

export const NotesCellRenderer = ({ value }: { value: string }) => (
  <Flex h="100%" alignItems="center">
    <TextBodyMedium isCharCountTruncate truncateCharCount={35}>
      {value}
    </TextBodyMedium>
  </Flex>
);
